import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { getRestApiUrl, getSpaceId } from "./configure";
import { isRequestTimeout, requestTimeoutMessage } from "./requestTimeout";
import { postFormWithSessionRetry, UPLOAD_RESPONSE_MS, UPLOAD_STALL_MS } from "./uploadTransport";
import { notifyWriteTimeout } from "./writeTimeoutSignal";
import { Pagination, SortItem } from "./commonTypes";

/** Minimal identity shape of whoever uploaded a file — enough for the media UI
 *  to show an uploader. Kept local so @em/api doesn't depend on the app's full
 *  User/auth types. */
export interface UploadedFileUploader {
  id?: string | null;
  username?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
}

// =============================================================================
// GraphQL Fragments
// =============================================================================

const UPLOADED_FILE_FIELDS = `
  id
  accountId
  userId
  sizeBytes
  filePath
  name
  originalFilename
  fileUrl
  thumbnailUrl
  mimeType
  notes
  status
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const UPLOADED_FILE_LIST_QUERY = gql`
  query UploadedFileList(
    $filter: UploadedFileFilter
    $params: PaginationAndSorting
  ) {
    uploadedFileList(filter: $filter, params: $params) {
      code
      data {
        ${UPLOADED_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const UPLOADED_FILE_GET_QUERY = gql`
  query UploadedFileGet($id: ID) {
    uploadedFileGet(id: $id) {
      code
      data {
        ${UPLOADED_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const UPLOADED_FILE_GET_MANY_QUERY = gql`
  query UploadedFileGetMany($ids: [ID]) {
    uploadedFileGetMany(ids: $ids) {
      code
      data {
        ${UPLOADED_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const UPLOADED_FILE_UPDATE_MUTATION = gql`
  mutation UploadedFileUpdate(
    $where: UploadedFileWhereInput
    $params: UploadedFileInput
  ) {
    uploadedFileUpdate(where: $where, params: $params) {
      code
      data {
        ${UPLOADED_FILE_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const UPLOADED_FILE_REMOVE_MUTATION = gql`
  mutation UploadedFileRemove($where: UploadedFileWhereInput) {
    uploadedFileRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const UPLOADED_FILE_REMOVE_MANY_MUTATION = gql`
  mutation UploadedFileRemoveMany($where: [UploadedFileWhereInput]) {
    uploadedFileRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface UploadedFile {
  id?: string;
  accountId?: string;
  userId?: string;
  sizeBytes?: number;
  filePath?: string;
  name?: string;
  originalFilename?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  mimeType?: string;
  notes?: string;
  status?: number;
  user?: UploadedFileUploader;
  createdAt?: string;

  // Self-describing media fields (ms_storage migration 040). Typed here for the
  // MediaSelector, but the list/get GraphQL selections only add them once the
  // storage subgraph is redeployed + the gateway recomposed. Until then they
  // arrive undefined; the library grid works off mimeType/fileUrl regardless.
  contextPath?: string | null;
  purpose?: string | null;
  kind?: string | null;
  uploadedByKind?: string | null;
  uploadedById?: string | null;
  uploadedByName?: string | null;
  orderNo?: number | null;
}

export interface UploadedFileFilter {
  id?: string[];
  accountId?: string[];
  userId?: string[];
  name?: string;
  filePath?: string;
  nameLike?: string;
  filePathLike?: string;
  status?: number[];

  // Server-side facets (ms_storage migration 040). The library and Event files both send `kind` here (#130 — the
  // library used to narrow the loaded page by mimeType in the browser).
  kind?: string[];
  contextPath?: string[];
  contextUnder?: string;
  purpose?: string[];
  uploadedByKind?: string[];
  uploadedById?: string[];

  /**
   * The brand's STANDARD library — ready-made files under ms_storage's nil account (the PartyLabz cover gallery) —
   * instead of the caller's own files. Folders are `filePath`. See the ms_storage README, "Standard files".
   */
  standard?: boolean;
}

export interface UploadedFileInput {
  filePath?: string | null | undefined;
  name?: string | null | undefined;
  originalFilename?: string | null | undefined;
  mimeType?: string | null | undefined;
  notes?: string | null | undefined;
  file64base?: string | null | undefined;
  thumbnailUrl?: string | null | undefined;
  status?: number | null | undefined;
  files?: File[];

  // Tag the upload with its owning context + role + uploader. create() appends
  // every defined field to the multipart body; ms_storage's controller forwards
  // them straight to the columns (kind is derived server-side from mime), so no
  // storage change is needed to persist these.
  contextPath?: string | null | undefined;
  purpose?: string | null | undefined;
  uploadedByKind?: string | null | undefined;
  uploadedById?: string | null | undefined;
  uploadedByName?: string | null | undefined;
  orderNo?: number | null | undefined;
}

export interface UploadedFileWhereInput {
  id?: string;
  accountId?: string;
  userId?: string;
  nameLike?: string;
  filePathLike?: string;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * UploadedFile service that handles all uploaded file-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class UploadedFileService {
  /**
   * Get a list of uploaded files with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: UploadedFileFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(UPLOADED_FILE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single uploaded file by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(UPLOADED_FILE_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple uploaded files by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(UPLOADED_FILE_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new uploaded file using REST API for file upload
   */
  static async create(params: UploadedFileInput): Promise<OpResult> {
    try {
      // Use REST API for file upload
      const formData = new FormData();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          if (key != "files") {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value[0]);
          }
        }
      });

      // Add file if provided
      // if (files && files.length > 0) {
      //   formData.append("files", files[0]);
      // }

      // ROADMAP #131 — abandoned when it stops making progress, never left waiting for good.
      const answer = await postFormWithSessionRetry({
        url: `${getRestApiUrl()}/uploaded-file`,
        body: formData,
        withCredentials: true, // Include cookies for authentication
        headers: {
          "x-space-id": getSpaceId(),
        },
        stallMs: UPLOAD_STALL_MS,
        responseMs: UPLOAD_RESPONSE_MS,
      });

      if (answer.status < 200 || answer.status > 299) {
        throw new Error(`HTTP error! status: ${answer.status}`);
      }

      return JSON.parse(answer.text);
    } catch (error) {
      if (isRequestTimeout(error)) {
        notifyWriteTimeout();

        return OpResult.fail(OP_RESULT_CODES.NETWORK_ERROR, {}, requestTimeoutMessage(error));
      }

      // Return error in the expected format
      return OpResult.fail(OP_RESULT_CODES.FAILED, {}, "Failed to upload file");
    }
  }

  /**
   * Update an existing uploaded file
   */
  static async update(
    where: UploadedFileWhereInput,
    params: UploadedFileInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      UPLOADED_FILE_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete an uploaded file
   */
  static async remove(where: UploadedFileWhereInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      UPLOADED_FILE_REMOVE_MUTATION,
      {
        where,
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple uploaded files
   */
  static async removeMany(where: UploadedFileWhereInput[]): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      UPLOADED_FILE_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}